/**
 * 57. Transfers — transfer order; ship/receive; in-transit status.
 *
 * Arabic and English (C7): every word from the catalogs; quantities are
 * numbers the locale writes, statuses agree with the transfer.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatDate, formatQuantity } from "@/lib/money";

interface TransferRow {
  id: string;
  transferNumber: string;
  fromWarehouse: string;
  toWarehouse: string;
  productSku: string;
  productName: string;
  quantity: string;
  transferDate: string | null;
  receivedDate: string | null;
  status: "Draft" | "InTransit" | "Received" | "Cancelled";
}

export function TransfersPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<TransferRow>(
    ["inventory", "transfers"],
    "/inventory/transfers",
  );

  return (
    <div>
      <PageHeader
        title={t("inventory.transfers.title")}
        subtitle={t("inventory.transfers.subtitle")}
        actions={
          <PendingButton
            variant="primary"
            reason="stockDocuments"
            slice="CO-W2"
          >
            {t("inventory.transfers.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("inventory.transfers.list"), data.total)}>
          <DataTable<TransferRow>
            rows={data.items}
            columns={[
              {
                key: "transferNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.transferNumber}</span>
                ),
              },
              { key: "fromWarehouse", label: t("fields.from") },
              { key: "toWarehouse", label: t("fields.to") },
              {
                key: "productSku",
                label: t("fields.sku"),
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              {
                key: "quantity",
                label: t("fields.quantity"),
                numeric: true,
                render: (r) => formatQuantity(r.quantity),
              },
              {
                key: "transferDate",
                label: t("inventory.transfers.shipped"),
                render: (r) =>
                  r.transferDate ? formatDate(r.transferDate) : "—",
              },
              {
                key: "receivedDate",
                label: t("fields.received"),
                render: (r) =>
                  r.receivedDate ? formatDate(r.receivedDate) : "—",
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) =>
                  r.status === "Received" ? (
                    <StatusBadge status={r.status} type="StockTransfer" />
                  ) : (
                    <Badge
                      tone={
                        r.status === "InTransit"
                          ? "primary"
                          : r.status === "Cancelled"
                            ? "danger"
                            : "default"
                      }
                    >
                      {r.status
                        ? statusLabel(t, "StockTransfer", r.status)
                        : "—"}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
